<template>
    <div data-test-view="goal-stats">
        <l-card-content>
            <!-- Header -->
            <l-headline :title="headline.title" :title-popover="headline.popover" :tabs="tabs" />
            <!--/Header -->

            <l-goal-filters />

            <!-- Global Stats -->
            <div>
                <div class="tw-grid tw-grid-cols-4">
                    <div class="tw-col-span-1 tw-flex tw-items-center">
                        <div>
                            <div>
                                <span class="tw-text-lg tw-font-semibold">Company</span>
                                <br />
                                {{ company.count }} Goals, Progress:
                                {{ company.progress.toFixed(0) }}%
                            </div>
                            <hr />
                            <div>
                                <span class="tw-font-semibold tw-text-lg">Teams</span>
                                <br />
                                {{ team.count }} Goals, Progress: {{ team.progress.toFixed(0) }}%
                            </div>
                            <hr />
                            <div>
                                <span class="tw-font-semibold tw-text-lg">Users</span>
                                <br />
                                {{ user.count }} Goals, Progress: {{ user.progress.toFixed(0) }}%
                            </div>
                        </div>
                    </div>
                    <div class="tw-col-span-3">
                        <l-goal-progress-chart
                            :range="{ start, end }"
                            :data="progressData"
                        ></l-goal-progress-chart>

                        <div class="tw-grid tw-grid-cols-2 tw-mt-4">
                            <div class="tw-col-span-1">
                                <l-date-picker
                                    v-model="start"
                                    wrapper-class="date-picker-wrapper"
                                    :label="''"
                                    :placeholder="''"
                                    :on-date-change="
                                        date => {
                                            if (date && date.getTime() != start.getTime()) {
                                                start = date;
                                                getProgress();
                                            }
                                        }
                                    "
                                />
                            </div>
                            <div class="tw-col-span-1">
                                <l-date-picker
                                    v-model="end"
                                    class="tw-float-right"
                                    wrapper-class="date-picker-wrapper"
                                    :label="''"
                                    :placeholder="''"
                                    :value="end"
                                    :on-date-change="
                                        date => {
                                            if (date && date.getTime() != end.getTime()) {
                                                end = date;
                                                getProgress();
                                            }
                                        }
                                    "
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!--/Global Stats -->
        </l-card-content>

        <!-- List -->
        <div>
            <div v-for="type in types" :key="type">
                <div v-if="goals[type].length">
                    <span v-if="type !== 'goalTeam'" class="pull-right explanation tw-pt-4 tw-pr-8">
                        <span
                            tabindex="0"
                            class="r-default-margin-s clickable"
                            :class="{
                                'color-primary': !['empty', 'non-empty'].includes(filterType[type]),
                            }"
                            @click="changeFilterType(type, 'all')"
                            @keydown.enter="changeFilterType(type, 'all')"
                        >
                            {{ $t(`goals.list.statsFilterAll`) }}
                        </span>
                        <span
                            tabindex="0"
                            class="r-default-margin-s clickable"
                            :class="{ 'color-primary': filterType[type] === 'empty' }"
                            @keydown.enter="changeFilterType(type, 'empty')"
                            @click="changeFilterType(type, 'empty')"
                        >
                            {{ $t(`goals.list.statsFilterEmpty`) }}
                        </span>
                        <span
                            tabindex="0"
                            class="r-default-margin-s clickable"
                            :class="{ 'color-primary': filterType[type] === 'non-empty' }"
                            @click="changeFilterType(type, 'non-empty')"
                            @keydown.enter="changeFilterType(type, 'non-empty')"
                        >
                            {{ $t(`goals.list.statsFilterNonEmpty`) }}
                        </span>
                    </span>
                    <l-card-content>
                        <div v-for="(goal, index) in goalType[type]" :key="index">
                            <l-goal-stat-list-item :goal="goal" @click="showGoalsList" />
                        </div>
                    </l-card-content>
                </div>
            </div>
        </div>
        <!--/List -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getListSegment, getListCustomFilter } from "@/utils/parse-goals-query";

import LCardContent from "@/components/LCardContent";
import LGoalStatListItem from "@/components/LGoalStatListItem";
import LHeadline from "@/components/LHeadline";
import LGoalFilters from "@/components/LGoalFilters";
import LDatePicker from "@/components/LDatePicker";
import LGoalProgressChart from "@/components/LGoalProgressChart";

import screenshotGoalsAnalyticsCircle from "@/img/screenshots/GoalsAnalyticsCircle.jpg";

export default {
    components: {
        LCardContent,
        LDatePicker,
        LGoalStatListItem,
        LHeadline,
        LGoalFilters,
        LGoalProgressChart,
    },
    props: {
        segment: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            start: this.$moment()
                .startOf("quarter")
                .toDate(),
            end: this.$moment()
                .endOf("quarter")
                .toDate(),
            types: ["goalTeam", "goalLocalTeam", "goalUser"],

            showMenu: true,

            goalType: {},
            filterType: () => {},
        };
    },

    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            goalStatsList: "goals/goals/statsList",
            goalStatsGlobal: "goals/goals/statsGlobal",
            goalStatsChartData: "goals/goals/statsChartData",
            goalStatsChartLabels: "goals/goals/statsChartLabels",
            goalProgress: "goals/goals/progress",
        }),
        headline() {
            switch (this.segment) {
                case "user":
                    return {
                        title: this.$t(`goals.list.titleMyGoals`),
                        popover: this.$t(`goals.list.subtitleMyGoals`),
                    };
                case "team":
                    return {
                        title: this.$t(`goals.list.titleMyTeam`),
                        popover: this.$t(`goals.list.subtitleMyTeam`),
                    };
                case "company":
                    return {
                        title: this.$t(`goals.list.titleCompanyTeam`),
                    };
                case "all":
                default:
                    return {
                        title: this.$t(`goals.list.titleCustomSelection`),
                    };
            }
        },

        goals() {
            const list = {};

            this.types.forEach(type => {
                list[type] = this.goalStatsList.filter(item => item.type === type);
            });

            return list;
        },
        progressData() {
            return (
                this.goalProgress?.map(update => ({
                    t: update.created_at,
                    y: update.newValue,
                })) || []
            );
        },
        tabs() {
            return [
                {
                    show: this.showMenu,
                    label: this.$t(`goals.list.hideMenu`),
                    icon: "fal fa-arrow-alt-to-left",
                    iconBeforeLabel: true,
                    testId: "goals-tree-hide-menu",
                    handler: this.toggleMenu,
                },
                {
                    show: !this.showMenu,
                    label: this.$t(`goals.list.showMenu`),
                    icon: "fal fa-arrow-alt-to-right",
                    iconBeforeLabel: true,
                    testId: "goals-tree-hide-menu",
                    handler: this.toggleMenu,
                },
                {
                    label: this.$t(`goals.list.btnExport`),
                    handler: () => this.exportGoalsList(),
                    show: true,
                    testId: "goals-export-btn",
                },
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    label: this.$t(`goals.list.btnCreate`),
                    handler: () => this.createNewGoal(),
                    show: true,
                    isButton: true,
                    tabClass: "btn-primary",
                },
            ];
        },
        company() {
            return this.goalStatsGlobal.byType?.goalTeam || { count: 0, progress: 0 };
        },
        team() {
            return this.goalStatsGlobal.byType?.goalLocalTeam || { count: 0, progress: 0 };
        },
        user() {
            return this.goalStatsGlobal.byType?.goalUser || { count: 0, progress: 0 };
        },
    },
    watch: {
        $route: {
            handler() {
                this.getProgress();
                for (let type of this.types) {
                    // reset all filters
                    this.changeFilterType(type, "all");
                }
            },
        },
        goals: {
            immediate: true,
            handler(goals) {
                for (let type of this.types) {
                    this.goalType[type] = goals[type];
                }
            },
        },
    },
    created() {
        this.$emit("section", "stats");
        this.getProgress();
        this.screenshotGoalsAnalyticsCircle = screenshotGoalsAnalyticsCircle;
    },
    methods: {
        changeFilterType(type, newFilterType) {
            let filter = Boolean;

            this.filterType[type] = newFilterType;

            if (this.filterType[type] === "all") {
                filter = Boolean;
            } else if (this.filterType[type] === "empty") {
                filter = goal => goal.count == 0;
            } else {
                filter = goal => goal.count != 0;
            }
            this.goalType[type] = this.goals[type].filter(filter);
            this.$forceUpdate();
        },
        createNewGoal() {
            this.$emit("create");
        },
        toggleMenu() {
            this.showMenu = !this.showMenu;
            this.$emit("toggleMenu");
        },
        async exportGoalsList() {
            await this.$store.dispatch({
                type: "goals/goals/export",
                payload: {
                    listSegment: getListSegment(this.segment),
                    listCustomFilter: getListCustomFilter(this.$route.query),
                    output: "exportStats",
                },
            });
        },
        async getProgress() {
            await this.$store.dispatch({
                type: "goals/goals/getProgress",
                payload: {
                    listSegment: getListSegment(this.segment),
                    listCustomFilter: getListCustomFilter(this.$route.query),
                    start: this.start.toISOString(),
                    end: this.end.toISOString(),
                    output: "reports",
                },
            });
        },
        showGoalsList({ segment = "all", filters = [] }) {
            const qs = Object.entries(
                filters.reduce((map, filter) => {
                    if (!map[filter.type]) {
                        map[filter.type] = [];
                    }
                    map[filter.type].push(filter.value);
                    return map;
                }, {}),
            )
                .map(entry => `${entry[0]}=${entry[1].join(",")}`)
                .join("&");

            this.$emit("section", "list");
            this.$router.push(`/goals/list/${segment}/?${qs}`);

            window.scrollTo({ top: 0, behavior: "smooth" });
        },
    },
};
</script>

<style scoped></style>
